import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiUpload } from '../../services/api'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { saveOfflineSubmission, saveOfflineAttachment } from '../../services/offline'
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Trash2,
  MapPin,
  Calendar,
  GraduationCap,
  Loader2,
  Plus,
  File,
  Camera,
  Play,
  Square,
  Timer,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface Submission {
  id: number
  submission_date: string
  status: string
  notes?: string
}

interface LocationData {
  latitude: number
  longitude: number
  accuracy: number
  address?: string
}

interface DocumentEntry {
  id: string
  file: File
  name: string
  size: number
}

interface PhotoEntry {
  id: string
  file: File
  preview: string
  capturedAt: Date
}

const MAX_DOCUMENTS = 10
const MAX_PHOTOS = 15

type TimerState = 'idle' | 'running' | 'stopped'

export default function C8SubmissionPage() {
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const documentInputRef = useRef<HTMLInputElement>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const [activePeriod, setActivePeriod] = useState<Period | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submissionCount, setSubmissionCount] = useState(0)
  const [calculatedPoints, setCalculatedPoints] = useState(0)

  const [documents, setDocuments] = useState<DocumentEntry[]>([])
  const [photos, setPhotos] = useState<PhotoEntry[]>([])
  const [notes, setNotes] = useState('')

  // Timer state
  const [timerState, setTimerState] = useState<TimerState>('idle')
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [endTime, setEndTime] = useState<Date | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  const [location, setLocation] = useState<LocationData | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Track if initial restore has been done (to prevent persist effect from clearing on mount)
  const hasRestoredRef = useRef(false)

  // Restore training timer state on mount FIRST (handles app being killed while timer running)
  useEffect(() => {
    const savedState = localStorage.getItem('c8_training_state')
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState)
        if (parsed.timerState === 'running' && parsed.startTime) {
          const savedStartTime = new Date(parsed.startTime)
          // Validate it's reasonable (not more than 24 hours ago)
          const hoursSinceStart = (Date.now() - savedStartTime.getTime()) / (1000 * 60 * 60)
          if (hoursSinceStart < 24) {
            setStartTime(savedStartTime)
            setTimerState('running')
            if (parsed.notes) setNotes(parsed.notes)
            setElapsedSeconds(Math.floor((Date.now() - savedStartTime.getTime()) / 1000))
          } else {
            localStorage.removeItem('c8_training_state')
          }
        }
      } catch {
        localStorage.removeItem('c8_training_state')
      }
    }
    hasRestoredRef.current = true
  }, [])

  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const submissionDate = currentDate.toISOString().split('T')[0]

  // Timer effect - uses real timestamps to survive screen lock/background
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null
    if (timerState === 'running' && startTime) {
      // Calculate immediately
      setElapsedSeconds(Math.floor((Date.now() - startTime.getTime()) / 1000))
      interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTime.getTime()) / 1000))
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [timerState, startTime])

  // Handle visibility change - recalculate timer when app returns from background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && timerState === 'running' && startTime) {
        // Recalculate elapsed time when app becomes visible again
        setElapsedSeconds(Math.floor((Date.now() - startTime.getTime()) / 1000))
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [timerState, startTime])

  // Persist training timer state to localStorage for recovery after screen lock
  useEffect(() => {
    // Don't clear localStorage during initial mount (before restore completes)
    if (!hasRestoredRef.current) return

    if (timerState === 'running' && startTime) {
      localStorage.setItem('c8_training_state', JSON.stringify({
        timerState,
        startTime: startTime.toISOString(),
        notes,
      }))
    } else if (timerState === 'idle') {
      localStorage.removeItem('c8_training_state')
    }
  }, [timerState, startTime, notes])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (activePeriod && indicator) {
      fetchSubmissions()
    }
  }, [activePeriod, indicator])

  const fetchData = async () => {
    try {
      const indicators = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
      const found = indicators.find(i => i.code === 'C.8')
      if (found) setIndicator(found)

      const periodsData = await apiGet<Period[]>('/kpi/periods')
      const currentMonth = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()

      let matchingPeriod = periodsData.find(
        p => p.month === currentMonth && p.year === currentYear && !p.is_locked
      )
      if (!matchingPeriod) {
        matchingPeriod = periodsData.find(p => !p.is_locked) || periodsData[0]
      }
      setActivePeriod(matchingPeriod || null)
    } catch (err) {
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    if (!activePeriod?.id || !indicator?.id) return
    try {
      const scoresData = await apiGet<{
        scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
      }>('/kpi/scores', { period_id: activePeriod.id })

      const score = scoresData.scores.find(s => s.indicator.code === 'C.8')
      if (score) {
        setSubmissionCount(score.submission_count)
        setCalculatedPoints(score.calculated_score)
      }

      const submissionsData = await apiGet<{ items: Submission[] }>('/kpi/submissions', {
        period_id: activePeriod.id,
        indicator_id: indicator.id,
        per_page: 10,
      })
      setSubmissions(submissionsData.items || [])
    } catch (err) {
      console.error('Failed to load submissions:', err)
    }
  }

  const fetchLocation = async (): Promise<LocationData | null> => {
    setLocationLoading(true)
    if (!navigator.geolocation) {
      setLocationLoading(false)
      return null
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const locationData: LocationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }
          if (isOnline) {
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
              )
              const data = await response.json()
              if (data.display_name) locationData.address = data.display_name
            } catch {}
          }
          setLocation(locationData)
          setLocationLoading(false)
          resolve(locationData)
        },
        () => { setLocationLoading(false); resolve(null) },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    })
  }

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    if (hrs > 0) {
      return `${hrs}h ${mins}m`
    }
    return `${mins} minutes`
  }

  const handleStartTraining = () => {
    const now = new Date()
    setStartTime(now)
    setTimerState('running')
    setElapsedSeconds(0)
    setError(null)
  }

  const handleStopTraining = () => {
    const now = new Date()
    setEndTime(now)
    setTimerState('stopped')
  }

  const handleResetTimer = () => {
    setTimerState('idle')
    setStartTime(null)
    setEndTime(null)
    setElapsedSeconds(0)
    // Clear persisted state
    localStorage.removeItem('c8_training_state')
  }

  const handleDocumentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (!selectedFiles) return

    const newDocuments: DocumentEntry[] = []
    const remainingSlots = MAX_DOCUMENTS - documents.length

    for (let i = 0; i < Math.min(selectedFiles.length, remainingSlots); i++) {
      const file = selectedFiles[i]
      if (file.size > 16 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds 16MB limit`)
        continue
      }
      newDocuments.push({ id: `doc_${Date.now()}_${i}`, file, name: file.name, size: file.size })
    }

    if (newDocuments.length > 0) {
      setDocuments([...documents, ...newDocuments])
      setError(null)
    }
    if (documentInputRef.current) documentInputRef.current.value = ''
  }

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (photos.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos allowed`)
      return
    }

    if (file.size > 16 * 1024 * 1024) {
      setError('Photo exceeds 16MB limit')
      return
    }

    const preview = URL.createObjectURL(file)
    const newPhoto: PhotoEntry = {
      id: `photo_${Date.now()}`,
      file,
      preview,
      capturedAt: new Date(),
    }

    setPhotos([...photos, newPhoto])
    setError(null)
    if (photoInputRef.current) photoInputRef.current.value = ''
  }

  const removeDocument = (docId: string) => {
    setDocuments(documents.filter(d => d.id !== docId))
  }

  const removePhoto = (photoId: string) => {
    const photo = photos.find(p => p.id === photoId)
    if (photo) {
      URL.revokeObjectURL(photo.preview)
    }
    setPhotos(photos.filter(p => p.id !== photoId))
  }

  const handleSubmit = async () => {
    if (!activePeriod || !indicator) { setError('No active period found'); return }
    if (timerState !== 'stopped') { setError('Please complete the training timer first'); return }
    if (photos.length === 0) { setError('Please capture at least one training photo'); return }

    setIsSubmitting(true)
    setError(null)

    const submissionLocation = await fetchLocation()
    const locationNote = submissionLocation
      ? `Location: ${submissionLocation.latitude.toFixed(6)}, ${submissionLocation.longitude.toFixed(6)}${submissionLocation.address ? ` (${submissionLocation.address})` : ''}`
      : 'Location: Not available'

    const durationNote = `Training Duration: ${formatDuration(elapsedSeconds)} (${formatTime(elapsedSeconds)})`
    const startTimeNote = startTime ? `Started: ${startTime.toLocaleTimeString()}` : ''
    const endTimeNote = endTime ? `Ended: ${endTime.toLocaleTimeString()}` : ''

    const fullNotes = [
      notes,
      '',
      durationNote,
      startTimeNote,
      endTimeNote,
      `Photos: ${photos.length}`,
      `Documents: ${documents.length}`,
      locationNote,
    ].filter(Boolean).join('\n')

    try {
      if (isOnline) {
        const response = await apiPost<{ id: number }>('/kpi/submissions', {
          period_id: activePeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: 1,
          notes: fullNotes,
          metadata: {
            location: submissionLocation,
            captured_at: currentDate.toISOString(),
            training_duration_seconds: elapsedSeconds,
            training_start: startTime?.toISOString(),
            training_end: endTime?.toISOString(),
            photo_count: photos.length,
            document_count: documents.length,
          },
        })

        if (response.id) {
          // Upload photos
          for (let i = 0; i < photos.length; i++) {
            const formData = new FormData()
            formData.append('file', photos[i].file)
            formData.append('submission_id', String(response.id))
            formData.append('file_type', `training_photo_${i + 1}`)
            await apiUpload('/kpi/attachments', formData)
          }
          // Upload documents
          for (let i = 0; i < documents.length; i++) {
            const formData = new FormData()
            formData.append('file', documents[i].file)
            formData.append('submission_id', String(response.id))
            formData.append('file_type', `training_doc_${i + 1}`)
            await apiUpload('/kpi/attachments', formData)
          }
        }
      } else {
        const clientId = await saveOfflineSubmission({
          client_id: `c8_${Date.now()}`,
          period_id: activePeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: 1,
          notes: fullNotes,
          created_at: new Date().toISOString(),
        })
        for (const photo of photos) {
          await saveOfflineAttachment(clientId, photo.file, photo.file.name, photo.file.type)
        }
        for (const doc of documents) {
          await saveOfflineAttachment(clientId, doc.file, doc.name, doc.file.type)
        }
      }

      setSuccess(true)
      // Cleanup photo previews
      photos.forEach(p => URL.revokeObjectURL(p.preview))
      setTimeout(() => {
        setDocuments([])
        setPhotos([])
        setNotes('')
        setLocation(null)
        setSuccess(false)
        handleResetTimer()
        fetchSubmissions()
      }, 2000)
    } catch (err) {
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) return <div className="mobile-page flex items-center justify-center"><LoadingSpinner size="lg" /></div>

  const progress = indicator ? Math.min((submissionCount / indicator.target) * 100, 100) : 0

  return (
    <div className="mobile-page bg-gray-50">
      {/* Hidden file inputs */}
      <input
        ref={documentInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
        multiple
        onChange={handleDocumentSelect}
      />
      <input
        ref={photoInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoCapture}
      />

      {/* Header */}
      <div className="bg-violet-500 px-4 pt-4 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/kpi')} className="mobile-back-btn">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-lg font-semibold">Training Record</h1>
            <p className="text-white/80 text-xs">C.8 - Training Documentation</p>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            <div className="bg-violet-100 rounded-xl p-3">
              <GraduationCap className="h-8 w-8 text-violet-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">{submissionCount} / {indicator?.target || 1}</span>
                <span className="text-sm font-semibold text-violet-500">{calculatedPoints.toFixed(2)} pts</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Max {indicator?.max_points || 10} points</p>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div className="bg-violet-500 rounded-full h-2 transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 mt-4 pb-32">
        {/* Timer Section */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className={`rounded-lg p-2 ${timerState === 'running' ? 'bg-green-100' : timerState === 'stopped' ? 'bg-violet-100' : 'bg-gray-100'}`}>
              <Timer className={`h-5 w-5 ${timerState === 'running' ? 'text-green-500' : timerState === 'stopped' ? 'text-violet-500' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Training Timer</p>
              <p className="font-bold text-2xl text-gray-900 font-mono">{formatTime(elapsedSeconds)}</p>
            </div>
            {timerState === 'stopped' && (
              <button onClick={handleResetTimer} className="text-xs text-gray-500 underline">Reset</button>
            )}
          </div>

          {timerState === 'idle' && (
            <button
              onClick={handleStartTraining}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              <Play className="h-5 w-5" />
              Start Training
            </button>
          )}

          {timerState === 'running' && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <p className="text-sm text-green-700">Training in progress...</p>
              </div>
              <button
                onClick={handleStopTraining}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                <Square className="h-5 w-5" />
                Stop Training
              </button>
            </div>
          )}

          {timerState === 'stopped' && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
              <p className="text-sm text-violet-700 font-medium">Training completed!</p>
              <p className="text-xs text-violet-600 mt-1">Duration: {formatDuration(elapsedSeconds)}</p>
              {startTime && <p className="text-xs text-violet-600">Started: {startTime.toLocaleTimeString()}</p>}
              {endTime && <p className="text-xs text-violet-600">Ended: {endTime.toLocaleTimeString()}</p>}
            </div>
          )}
        </div>

        {/* Date Display */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-violet-100 rounded-lg p-2">
              <Calendar className="h-5 w-5 text-violet-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Submission Date (Auto)</p>
              <p className="font-medium text-gray-900">{formattedDate}</p>
            </div>
          </div>
        </div>

        {/* Location Display */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${location ? 'bg-green-100' : 'bg-gray-100'}`}>
              {locationLoading ? (
                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
              ) : (
                <MapPin className={`h-5 w-5 ${location ? 'text-green-500' : 'text-gray-400'}`} />
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Location (Captured on Submit)</p>
              {locationLoading ? (
                <p className="text-sm text-gray-600">Getting location...</p>
              ) : location ? (
                <div>
                  <p className="font-medium text-gray-900 text-sm">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
                  {location.address && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{location.address}</p>}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Will be captured when you submit</p>
              )}
            </div>
          </div>
        </div>

        {/* Photo Capture Section */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Training Photos
              <span className="text-red-500">*</span>
            </label>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${photos.length >= MAX_PHOTOS ? 'bg-red-100 text-red-600' : 'bg-violet-100 text-violet-600'}`}>
              {photos.length} / {MAX_PHOTOS}
            </span>
          </div>

          {/* Photo Grid */}
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {photos.map((photo, index) => (
                <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img src={photo.preview} alt={`Training ${index + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                    {index + 1}
                  </div>
                  <button
                    onClick={() => removePhoto(photo.id)}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {photos.length < MAX_PHOTOS && (
            <button
              onClick={() => photoInputRef.current?.click()}
              className="mobile-upload-area border-violet-200"
            >
              <Camera className="h-8 w-8 text-violet-400 mb-2" />
              <span className="text-gray-600 font-medium">Take Training Photo</span>
              <span className="text-gray-400 text-xs mt-1">Camera only - no gallery</span>
              <span className="text-violet-600 text-xs mt-1 font-medium">{MAX_PHOTOS - photos.length} photos remaining</span>
            </button>
          )}

          {photos.length >= MAX_PHOTOS && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
              <p className="text-sm text-yellow-700">Maximum {MAX_PHOTOS} photos reached</p>
            </div>
          )}
        </div>

        {/* Document Upload Section */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Training Documents
              <span className="text-gray-400 text-xs font-normal">(Optional)</span>
            </label>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${documents.length >= MAX_DOCUMENTS ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
              {documents.length} / {MAX_DOCUMENTS}
            </span>
          </div>

          {documents.length > 0 && (
            <div className="space-y-2 mb-3">
              {documents.map((doc, index) => (
                <div key={doc.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <div className="bg-violet-100 rounded-lg p-2">
                    <File className="h-5 w-5 text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{index + 1}. {doc.name}</p>
                    <p className="text-xs text-gray-500">{(doc.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button onClick={() => removeDocument(doc.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {documents.length < MAX_DOCUMENTS && (
            <button onClick={() => documentInputRef.current?.click()} className="mobile-upload-area">
              <Plus className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-gray-600 font-medium">{documents.length === 0 ? 'Upload Training Materials' : 'Add More Documents'}</span>
              <span className="text-gray-400 text-xs mt-1">PDF, Word, Excel or Image (max 16MB each)</span>
              <span className="text-gray-500 text-xs mt-1">{MAX_DOCUMENTS - documents.length} slots remaining</span>
            </button>
          )}

          {documents.length >= MAX_DOCUMENTS && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
              <p className="text-sm text-yellow-700">Maximum {MAX_DOCUMENTS} documents reached</p>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about the training session..."
            rows={3}
            className="mobile-textarea"
          />
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-sm text-green-700">{isOnline ? 'Training record submitted!' : 'Saved offline.'}</p>
          </div>
        )}

        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            <p className="text-sm text-yellow-700">You're offline. Will sync later.</p>
          </div>
        )}

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Submissions
            </h3>
            <div className="space-y-2">
              {submissions.slice(0, 5).map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                  <div className="bg-violet-100 rounded-lg p-2">
                    <GraduationCap className="h-5 w-5 text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(sub.submission_date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                    {sub.notes && <p className="text-xs text-gray-500 truncate">{sub.notes.split('\n')[0]}</p>}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                    sub.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="mobile-submit-container">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || timerState !== 'stopped' || photos.length === 0}
          className="mobile-submit-btn bg-violet-500 hover:bg-violet-600 disabled:bg-gray-300"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Submitting...
            </span>
          ) : timerState !== 'stopped' ? (
            'Complete Training Timer First'
          ) : photos.length === 0 ? (
            'Take Training Photos'
          ) : (
            `Submit C.8 (${photos.length} photos, ${documents.length} docs)`
          )}
        </button>
      </div>
    </div>
  )
}
